
import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';

export type ThemePreset = 'default' | 'ocean' | 'sunrise' | 'forest' | 'purple' | 'neutral';

interface ThemeController {
  // Current theme state
  theme: 'light' | 'dark' | 'system' | undefined;
  preset: ThemePreset;
  resolvedTheme: 'light' | 'dark' | undefined;
  
  // Theme actions
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setPreset: (preset: ThemePreset) => void;
  toggleTheme: () => void;
  
  // Theme utilities
  isDark: boolean;
  isLight: boolean;
  isSystem: boolean;
  mounted: boolean;
  
  // Available options
  availableThemes: Array<{ value: 'light' | 'dark' | 'system'; label: string }>;
  availablePresets: Array<{ value: ThemePreset; label: string; description: string }>;
}

const THEME_PRESET_KEY = 'theme-preset';

export const useThemeController = (): ThemeController => {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [preset, setPresetState] = useState<ThemePreset>('default');
  const [mounted, setMounted] = useState(false);

  // Load preset from localStorage on mount
  useEffect(() => {
    setMounted(true);
    const savedPreset = localStorage.getItem(THEME_PRESET_KEY) as ThemePreset;
    if (savedPreset && ['default', 'ocean', 'sunrise', 'forest', 'purple', 'neutral'].includes(savedPreset)) {
      setPresetState(savedPreset);
      applyPresetClass(savedPreset);
    }
  }, []);

  // Apply preset class to document
  const applyPresetClass = (newPreset: ThemePreset) => {
    const html = document.documentElement;
    
    // Remove all existing theme preset classes
    html.classList.remove('theme-ocean', 'theme-sunrise', 'theme-forest', 'theme-purple', 'theme-neutral');
    
    // Apply new preset class (except for default)
    if (newPreset !== 'default') {
      html.classList.add(`theme-${newPreset}`);
    }
    
    console.log('Applied theme preset:', newPreset, 'Classes:', html.classList.toString());
  };

  const setPreset = (newPreset: ThemePreset) => {
    setPresetState(newPreset);
    applyPresetClass(newPreset);
    localStorage.setItem(THEME_PRESET_KEY, newPreset);
  };

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  const availableThemes = [
    { value: 'light' as const, label: 'Light' },
    { value: 'dark' as const, label: 'Dark' },
    { value: 'system' as const, label: 'System' },
  ];

  const availablePresets = [
    { 
      value: 'default' as const, 
      label: 'Default', 
      description: 'Professional blue with warm amber accents' 
    },
    { 
      value: 'ocean' as const, 
      label: 'Ocean', 
      description: 'Deep blues with cyan highlights and sunlit sand warmth' 
    },
    { 
      value: 'sunrise' as const, 
      label: 'Sunrise', 
      description: 'Bold reds with orange warmth and bright sky teal contrast' 
    },
    { 
      value: 'forest' as const, 
      label: 'Forest', 
      description: 'Natural greens with earthy golden moss accents' 
    },
    { 
      value: 'purple' as const, 
      label: 'Purple', 
      description: 'Rich purples with violet tints and burnished peach highlights' 
    },
    { 
      value: 'neutral' as const, 
      label: 'Neutral', 
      description: 'Sophisticated grays with muted ochre warmth' 
    },
  ];

  return {
    // Current state
    theme: theme as 'light' | 'dark' | 'system' | undefined,
    preset,
    resolvedTheme: resolvedTheme as 'light' | 'dark' | undefined,
    
    // Actions
    setTheme,
    setPreset,
    toggleTheme,
    
    // Utilities
    isDark: resolvedTheme === 'dark',
    isLight: resolvedTheme === 'light',
    isSystem: theme === 'system',
    mounted,
    
    // Options
    availableThemes,
    availablePresets,
  };
};

export default useThemeController;
